import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr, fmt } from "@/lib/api";
import { Card } from "@/components/kit";
import { Coins, User, Truck, Package, Check, Lock } from "lucide-react";
import { toast } from "sonner";

function RateRow({ name, sub, value, onSave, testid, readOnly }) {
  const [v, setV] = useState(value ?? 0);
  const [saved, setSaved] = useState(false);
  useEffect(() => setV(value ?? 0), [value]);
  const save = async () => {
    await onSave(Number(v));
    setSaved(true); setTimeout(() => setSaved(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3">
      <div className="min-w-0">
        <div className="font-medium text-white truncate">{name}</div>
        {sub && <div className="text-xs text-slate-500">{sub}</div>}
      </div>
      <div className="flex items-center gap-2">
        <div className="relative">
          <input data-testid={testid} type="number" step="0.01" value={v} disabled={readOnly} onChange={(e) => setV(e.target.value)}
            className="w-32 rounded-lg bg-slate-950/60 border border-slate-700 px-3 py-2 text-right text-sm text-slate-100 outline-none focus:border-amber-500 disabled:opacity-60 disabled:cursor-not-allowed" />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">SEK</span>
        </div>
        {!readOnly && (
          <button data-testid={`${testid}-save`} onClick={save}
            className={`rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${saved ? "bg-emerald-500 text-white" : "bg-amber-500 text-slate-950 hover:bg-amber-400"}`}>
            {saved ? <Check size={14} /> : "Spara"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Prices() {
  const { user } = useAuth();
  const readOnly = user?.role !== "owner";
  const [users, setUsers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [materials, setMaterials] = useState([]);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/machines").then((r) => setMachines(r.data));
    api.get("/materials").then((r) => setMaterials(r.data));
  };
  useEffect(() => { load(); }, []);

  const saveUser = async (id, rate) => { try { await api.put(`/users/${id}`, { hourly_rate: rate }); toast.success("Timpris sparat"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const saveMachine = async (m, rate) => { try { await api.put(`/machines/${m.id}`, { name: m.name, unit: m.unit || "h", hourly_rate: rate }); toast.success("Timpris sparat"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const saveMaterial = async (m, cost) => { try { await api.put(`/materials/${m.id}`, { name: m.name, unit: m.unit || "st", unit_cost: cost }); toast.success("Á-pris sparat"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="rise">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-amber-500">{readOnly ? "Endast läsning" : "Endast ägare"}</p>
        <h1 className="font-display text-3xl font-extrabold text-white">Priser</h1>
        <p className="mt-1 text-sm text-slate-400">Prissättning används för att räkna ut belopp. Anställda ser aldrig priser.</p>
        {readOnly && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-950/40 px-3 py-2 text-xs text-slate-400" data-testid="prices-readonly-banner">
            <Lock size={13} className="text-amber-400" /> Som delägare kan du se priserna men endast ägaren kan ändra dem.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-800 px-5 py-4"><User size={16} className="text-amber-400" /><h2 className="font-display font-bold text-white">Timpris personal</h2></div>
          <div className="divide-y divide-slate-800/60">
            {users.map((u) => (
              <RateRow key={u.id} name={u.name} sub={u.role === "owner" ? "Ägare" : u.role === "partner" ? "Delägare" : "Anställd"} value={u.hourly_rate} testid={`price-emp-${u.id}`} readOnly={readOnly} onSave={(r) => saveUser(u.id, r)} />
            ))}
          </div>
        </Card>

        <Card className="overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-800 px-5 py-4"><Truck size={16} className="text-sky-400" /><h2 className="font-display font-bold text-white">Timpris maskiner</h2></div>
          <div className="divide-y divide-slate-800/60">
            {machines.map((m) => (
              <RateRow key={m.id} name={m.name} sub={`per ${m.unit || "h"}`} value={m.hourly_rate} testid={`price-mac-${m.id}`} readOnly={readOnly} onSave={(r) => saveMachine(m, r)} />
            ))}
            {machines.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-500">Inga maskiner.</div>}
          </div>
        </Card>

        <Card className="overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-800 px-5 py-4"><Package size={16} className="text-violet-400" /><h2 className="font-display font-bold text-white">Á-pris material</h2></div>
          <div className="divide-y divide-slate-800/60">
            {materials.map((m) => (
              <RateRow key={m.id} name={m.name} sub={`per ${m.unit}`} value={m.unit_cost} testid={`price-mat-${m.id}`} readOnly={readOnly} onSave={(r) => saveMaterial(m, r)} />
            ))}
            {materials.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-500">Inget material.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
